import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell } from '../components/AdminShared';

export default function AdminLanguageRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const loadRequests = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.listLanguageRequests(params);
      setRequests(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleApprove = async (id) => {
    try {
      await adminAPI.approveLanguageRequest(id);
      toast.success('Language request approved');
      loadRequests();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReject = async (id) => {
    try {
      await adminAPI.rejectLanguageRequest(id);
      toast.success('Language request rejected');
      loadRequests();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject'); }
  };

  const statusIcon = (s) => s === 'approved' ? <CheckCircle className="w-3.5 h-3.5 text-green-500 inline mr-1" /> : s === 'rejected' ? <XCircle className="w-3.5 h-3.5 text-red-500 inline mr-1" /> : <Clock className="w-3.5 h-3.5 text-amber-500 inline mr-1" />;

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell>
      <div className="p-6 md:p-8 max-w-6xl animate-fade-in" data-testid="admin-language-requests-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'DM Sans' }}>Language Requests</h1>
            <p className="text-muted-foreground mt-1 text-sm">Review and approve interpreter language requests</p>
          </div>
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="w-40" data-testid="lang-req-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Interpreter</TableHead>
              <TableHead>Language</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {requests.map(req => (
                <TableRow key={req.id} data-testid={`lang-req-admin-row-${req.id}`}>
                  <TableCell className="font-medium text-sm">{req.interpreter_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{req.language_name} ({req.language_code})</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{req.notes || '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><span className="text-xs capitalize">{statusIcon(req.status)}{req.status}</span></TableCell>
                  <TableCell>
                    {req.status === 'pending' && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="text-green-600 h-8 w-8" onClick={() => handleApprove(req.id)} data-testid={`approve-lang-req-${req.id}`}>
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => handleReject(req.id)} data-testid={`reject-lang-req-${req.id}`}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {requests.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No language requests found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminPageShell>
  );
}
